import { useEffect } from 'react';
import { InjectedIntl } from 'react-intl';
import { useEligibilityGuard } from './hooks/useEligibilityGuard';
import { AcmEligibilityResponse } from './types';
import { getTextFactory } from 'utils/TextUtils';
import { Alert } from '@nbc-design/alert';
import './styles.scss';
import { scrollIntoView } from 'utils/domUtils';

export type EligibilityRulesAlertProps = {
  intl: InjectedIntl;
  acmEligibilityResponses: AcmEligibilityResponse[] | undefined;
};

const AccountEligibilityRulesAlert = ({ intl, acmEligibilityResponses }: EligibilityRulesAlertProps) => {
  const getAccessibilityText = getTextFactory(intl, 'accessibility');
  const getEligibilityRuleText = getTextFactory(intl, 'accountEligibility');
  const getGlobalText = getTextFactory(intl, 'global');

  const { eligibilityErrors } = useEligibilityGuard({ getGlobalText, acmEligibilityResponses });

  const hasErrors = eligibilityErrors?.length > 0;

  useEffect(() => {
    if (hasErrors) {
      scrollIntoView('#eligibility-error-alert');
    }
  }, [hasErrors, acmEligibilityResponses]);

  if (!hasErrors) {
    return null;
  }

  return (
    <Alert
      id="eligibility-error-alert"
      appearance="error"
      title={getEligibilityRuleText('title')}
      description={
        <ul className="eligibility-rules">
          {eligibilityErrors.map(({ ruleCode, messageKey, failedAccounts }) => (
            <li key={ruleCode} className="eligibility-rule-item">
              {getEligibilityRuleText(messageKey)}
              {failedAccounts.length > 0 && (
                <ul className="eligibility-accounts">
                  {failedAccounts.map((account, idx) => (
                    <li key={`${ruleCode}-${idx}`}>{account}</li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      }
      ariaLabelIcon={getAccessibilityText('icon.error')}
      dataTest="alert_eligibilityError"
      className="mb-4"
    />
  );
};

export default AccountEligibilityRulesAlert;
